import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { MapPin, MessageCircle, Phone } from 'lucide-react';
import { APP_CONFIG } from '../../config/app';
import TrackedContactLink from '../TrackedContactLink';
import HeroPhoto from './HeroPhoto';
import ReviewsBadge from './ReviewsBadge';
import RoutingCard from './RoutingCard';
import { ROUTING_CARDS } from './data';

/** Hero d'accueil : aiguille le visiteur vers l'une des trois activités. */
const HomeHero: React.FC = () => {
  const heroRef = useRef<HTMLElement>(null);
  const { whatsappUrl, phoneTel, phoneDisplay } = APP_CONFIG.contact;

  return (
    <section id="home" ref={heroRef} className="relative overflow-hidden bg-canvas pt-16 md:pt-20">
      <div className="hero-halo pointer-events-none absolute inset-0 z-0" aria-hidden="true" />

      <div className="container-site relative z-10 pb-section-sm pt-6 md:pb-section md:pt-14">
        <div className="flex flex-col items-center gap-12 lg:flex-row lg:gap-14">
          <div className="w-full lg:w-[60%]">
            <div className="section-subtitle-badge">
              <MapPin size={14} className="shrink-0" aria-hidden />
              <span>Viry · Genève · Pays de Gex · Haute-Savoie</span>
            </div>
            <h1 className="heading-display mb-4 !text-[26px] sm:!text-[32px] md:mb-5 md:!text-[clamp(32px,4.2vw+16px,58px)] lg:!text-[44px]">
              Réparation téléphone &amp; dépannage informatique{' '}
              <span className="text-accent-soft">à domicile</span> à Viry, près de Genève
            </h1>
            <p className="text-lead mb-6 !max-w-[36em] md:mb-8">
              Réparation de téléphone et dépannage informatique à domicile, création de sites pour les pros. Un seul
              interlocuteur local, prix annoncé avant toute intervention.
            </p>

            <ul className="grid grid-cols-1 gap-4 pt-1 md:grid-cols-3 lg:grid-cols-1">
              {ROUTING_CARDS.map((card, index) => (
                <motion.li
                  key={card.id}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.1 + index * 0.08, ease: 'easeOut' }}
                >
                  <RoutingCard {...card} />
                </motion.li>
              ))}
            </ul>

            <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-5">
              <TrackedContactLink
                channel="whatsapp"
                service="autre"
                ctaLocation="hero"
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary"
              >
                <MessageCircle size={16} className="text-accent-soft" aria-hidden />
                Une question&nbsp;? Écrivez-moi sur WhatsApp
              </TrackedContactLink>
              <TrackedContactLink
                channel="phone"
                service="autre"
                ctaLocation="hero"
                href={`tel:${phoneTel}`}
                className="price inline-flex min-h-11 items-center justify-center gap-2 text-fg transition-colors hover:text-accent-soft sm:justify-start"
              >
                <Phone size={16} className="text-accent-soft" aria-hidden />
                {phoneDisplay}
              </TrackedContactLink>
            </div>

            <div className="mt-5">
              <ReviewsBadge />
            </div>
            <p className="mt-3 font-mono text-xs leading-relaxed text-fg-muted sm:text-sm">
              Garantie 90 jours · Déplacement inclus · Diagnostic gratuit
            </p>
          </div>

          {/* Photo (ou mockup WhatsApp en repli) : desktop uniquement, les cartes passent avant en mobile/tablette */}
          <div className="hidden w-full justify-center lg:flex lg:w-[40%]">
            <HeroPhoto scrollTarget={heroRef} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeHero;
